import { Controller } from "react-hook-form";
import "react-phone-number-input/style.css";

interface PhoneInputProps {
  control: any;
  name: string;
  error?: string;
  label?: string;
  defaultValue?: string;
  disabled?: boolean;
}

const PhoneInput = ({
  control,
  name,
  error,
  label = "رقم الجوال",
  defaultValue,
  disabled = false,
}: PhoneInputProps) => {
  return (
    <div>
      <label className='block text-right text-gray-700 mb-1'>{label}</label>
      {disabled ? (
        <input
          type='text'
          readOnly
          value={defaultValue || "غير متوفر"}
          className='w-full py-3 px-4 border rounded-lg bg-gray-100 text-left text-gray-700'
          dir='ltr'
        />
      ) : (
        // Make the whole input group LTR
        <div
          className={`flex items-center rounded-lg overflow-hidden ${
            error ? "border border-red-600 bg-red-50" : "border bg-gray-100"
          }`}
          dir='ltr'
        >
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              // Map stored value or pasted value to raw digits up to 10 without auto-adding any leading zero
              const rawVal = (field.value ?? defaultValue ?? "") as string;
              const toLocal = (raw: string) => {
                let d = String(raw).replace(/\D/g, "");
                // Handle country code prefixes
                if (d.startsWith("009665")) d = d.slice(5);
                else if (d.startsWith("00966")) d = d.slice(5);
                else if (d.startsWith("966")) d = d.slice(3);
                // Do NOT auto-prepend any zero; keep exactly what user typed
                if (d.length > 10) d = d.slice(0, 10);
                return d;
              };

              const local = toLocal(rawVal); // up to 10 digits for display

              const normalizeToLocal = (input: string) => {
                let digits = (input || "").replace(/\D/g, "");
                // Remove country codes if pasted
                if (digits.startsWith("009665")) digits = digits.slice(5);
                else if (digits.startsWith("00966")) digits = digits.slice(5);
                else if (digits.startsWith("966")) digits = digits.slice(3);
                // Do NOT auto-add or force a leading zero
                if (digits.length > 10) digits = digits.slice(0, 10);
                return digits;
              };

              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const local = normalizeToLocal(e.target.value);
                field.onChange(local);
              };

              const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const text = e.clipboardData.getData("text") || "";
                const local = normalizeToLocal(text);
                field.onChange(local);
              };

              return (
                <div className='flex items-center w-full' dir='ltr'>
                  <span
                    className='px-3 py-3 bg-gray-200 text-gray-700 select-none pointer-events-none border-r'
                    dir='ltr'
                  >
                    +966
                  </span>
                  <input
                    ref={field.ref}
                    name={name}
                    onBlur={field.onBlur}
                    type='tel'
                    inputMode='numeric'
                    dir='ltr'
                    placeholder='XXXXXXXXXX'
                    value={local}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    maxLength={10}
                    pattern='^\d{0,10}$'
                    className={`flex-1 py-3 px-4 rounded-lg text-left bg-transparent outline-none ${
                      error ? "text-red-900 placeholder:text-red-400" : "text-gray-800"
                    }`}
                    aria-invalid={!!error}
                  />
                </div>
              );
            }}
          />
        </div>
      )}
      {error && <p className='text-sm text-red-600 mt-1'>{error}</p>}
    </div>
  );
};

export default PhoneInput;
